import { useCallback, useMemo } from 'react';

import { ICombo, IModifierSelection } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail/utils';
import { selectors, useAppSelector } from 'state/global-state';
import usePricingFunction from 'state/menu-options/hooks/use-pricing-function';
import { useOffersContext } from 'state/offers';
import { useProductWizardContext } from 'state/product-wizard';
import { useUIContext } from 'state/ui';
import { IWithVendorConfig, getOfferVendorConfigForPricing } from 'utils/vendor-config';

import usePosVendor from './use-pos-vendor';

interface ProductPriceConditions {
  shouldIncludeModifications: boolean;
  shouldIncludeQuantity: boolean;
}

export const useProductPrice = (conditions?: ProductPriceConditions) => {
  const { vendor } = usePosVendor();
  const { pricingFunction } = usePricingFunction({ vendor });
  const { selectedOffer: selectedLegacyOffer } = useOffersContext();
  const { productQuantity, selectedProduct, userSelections } = useProductWizardContext();
  const { formatCurrencyForLocale } = useUIContext();

  const { incentiveNotInMenu } = useRedeemReward();

  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

  const selectedOffer = selectedLoyaltyOffer || selectedLegacyOffer;

  const { shouldIncludeModifications = false, shouldIncludeQuantity = false } = conditions || {};

  const totalPrice = useCallback(
    (modifiersInjected?: IModifierSelection[]) => {
      const quantity = shouldIncludeQuantity ? productQuantity : 1;
      const baseTotal = pricingFunction(selectedProduct, quantity);

      let total = baseTotal;
      if (selectedOffer) {
        const selectedProductVendorConfigs = selectedProduct.vendorConfigs;
        const offerVendorConfigs = getOfferVendorConfigForPricing(selectedOffer);
        const selectedProductPrice = selectedProductVendorConfigs
          ? pricingFunction(
              { vendorConfigs: selectedProductVendorConfigs } as IWithVendorConfig,
              quantity,
              modifiersInjected
            )
          : 0;
        const selectedOfferPrice = offerVendorConfigs
          ? pricingFunction(
              { vendorConfigs: offerVendorConfigs } as IWithVendorConfig,
              quantity,
              modifiersInjected
            )
          : 0;
        total = selectedProductPrice || selectedOfferPrice || selectedOffer?.offerPrice || 0;
      }

      if (shouldIncludeModifications) {
        const comboSlotSelections = transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        );
        const modifierSelections =
          transformUserSelectionsModifierToModifierSelection(userSelections);
        const extraTotal =
          pricingFunction(selectedProduct, quantity, modifierSelections, comboSlotSelections) -
          baseTotal;

        total += extraTotal;
      }
      return total;
    },
    [
      pricingFunction,
      productQuantity,
      selectedOffer,
      selectedProduct,
      shouldIncludeModifications,
      shouldIncludeQuantity,
      userSelections,
    ]
  );

  const formattedProductPrice = useMemo(() => {
    const totalWithDiscount = incentiveNotInMenu ? 0 : totalPrice();
    return formatCurrencyForLocale(totalWithDiscount);
  }, [formatCurrencyForLocale, incentiveNotInMenu, totalPrice]);

  return {
    totalPrice,
    formattedProductPrice,
  };
};
